<template>
  <div :class="[$options.name, statusClassList]" :data-e-ga-id="uniqueId">
    <div :for="label" @click="handleCheck">
      <input :id="label" :class="$options.name + '__checkbox-input'" :name="label" type="checkbox" />
      <span :class="[$options.name + '__checkbox', { checked: isChecked }, { disabled: disabled }]">
        <font-awesome-icon
          v-if="isChecked && type === 'default'"
          :class="[$options.name + '__checkbox-icon', 'check']"
          icon="fa-check"
        />
        <font-awesome-icon
          v-if="isChecked && type === 'indeterminate'"
          :class="[$options.name + '__checkbox-icon', 'hyphen']"
          icon="fa-hyphen"
        />
      </span>
    </div>
    <div v-if="label || helper" class="tw:cursor-pointer tw:ml-2 tw:flex tw:flex-col tw:gap-1" @click="handleCheck">
      <p v-if="label" class="tw:body-1 tw:-mt-1">{{ label }}</p>
      <p v-if="helper" class="tw:body-2 tw:text-default-700">
        {{ helper }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneCheckbox",
  props: {
    /**
     * Whether the type of checkbox is default or indeterminate (to make parent/child checkboxes)
     */
    type: {
      type: String as PropType<"default" | "indeterminate">,
      default: "default",
    },
    /**
     * Whether the checkbox is checked or not
     */
    checked: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the button should be rendered as disabled and not clickable
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Label of the checkbox, displayed next to the checkbox, bigger than the helper text
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Helper text of the checkbox, displayed below the label, smaller than the label
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * Whether the toggle is controlled by the parent component
     * controlled: false: The component is "uncontrolled," managing its internal state.
     * controlled: true: The component is "controlled," and its value is managed by the parent through props.
     */
    controlled: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: [
    /**
     * Emits when the checkbox is checked or unchecked, new state is emitted as a boolean argument
     */
    "change",
  ],
  setup(props, { emit }) {
    const isChecked = ref(props.checked);

    const statusClassList = computed(() => {
      return {
        "EdroneCheckbox--default": props.type === "default",
        "EdroneCheckbox--indeterminate": props.type === "indeterminate",
      };
    });

    watch(
      () => props.checked,
      (newValue) => {
        isChecked.value = newValue;
      }
    );

    function handleCheck() {
      if (props.disabled) return;
      const newCheckedState = !isChecked.value;
      emit("change", newCheckedState);
      gtagUniversalSend({ component_id: props.uniqueId });
      if (props.controlled) {
        isChecked.value = props.checked;
      } else {
        isChecked.value = newCheckedState;
      }
    }

    return {
      isChecked,
      statusClassList,
      handleCheck,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneCheckbox {
  display: flex;
}

.EdroneCheckbox__checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.EdroneCheckbox__checkbox {
  cursor: pointer;
  display: block;
  position: relative;
  @apply tw:w-4 tw:h-4 tw:rounded-sm tw:bg-white tw:border tw:border-default-200;
}

.EdroneCheckbox__checkbox:hover {
  @apply tw:bg-default-100 tw:border tw:border-default-400;
}

.EdroneCheckbox__checkbox.checked {
  @apply tw:bg-blue-500 tw:border tw:border-blue-500;
}

.EdroneCheckbox__checkbox.checked:hover {
  @apply tw:bg-blue-600 tw:border tw:border-blue-600;
}

.EdroneCheckbox__checkbox.disabled {
  cursor: not-allowed;
  @apply tw:bg-default-000 tw:border tw:border-default-100;
}

.EdroneCheckbox__checkbox.disabled:hover {
  @apply tw:bg-default-000 tw:border tw:border-default-100;
}

.EdroneCheckbox__checkbox.disabled.checked {
  @apply tw:bg-default-100 tw:border tw:border-default-100;
}

.EdroneCheckbox__checkbox-icon {
  position: absolute;
  @apply tw:text-white;
}

.EdroneCheckbox__checkbox-icon.check {
  @apply tw:top-px tw:left-0.5 tw:text-xs;
}

.EdroneCheckbox__checkbox-icon.hyphen {
  @apply tw:text-base tw:-top-px tw:left-0.5;
}
</style>
